import { Box, Stack } from "@mui/material";
import { useState, useMemo } from "react";
import { useParams } from "react-router";
import { useTenant } from "hooks/settings";
import {
  useGetMaterialExamplesQuery,
  MaterialEventPair,
} from "src/store/api/generatedApi";
import { VideoControlProvider } from "components/scrapEyeDataDashboard/VideoPlayer/contexts/VideoControlContext";
import { useAreaLabelledEvents } from "components/scrapEyeLabelling/hooks/useAreaLabel";
import dayjs from "dayjs";
import utc from "dayjs/plugin/utc";

dayjs.extend(utc);
import { ScrapEyesEvent } from "components/scrapEyeLabelling/types";
import { getEventLabel } from "src/utils/eventUtils";
import { ScrapEyesEventsPanel } from "components/identifyEventsTool/tool/ScrapEyesEventsPanel";
import { useMaterials } from "hooks/useMaterials";
import { calculateVideoWindow } from "./utils/videoWindowUtils";
import { LoadingState, EmptyState } from "./components/ExamplePlayerStates";
import { ExampleNavigationControls } from "./components/ExampleNavigationControls";
import { ExampleVideoPlayerContent } from "./components/ExampleVideoPlayerContent";

type MaterialExampleVideoPlayerProps = Readonly<{
  materialName: string;
}>;

export const MaterialExampleVideoPlayer = ({
  materialName,
}: MaterialExampleVideoPlayerProps) => {
  const { location } = useParams<{ location: string }>();
  const withAreaLabels = useAreaLabelledEvents(location);
  const tenant = useTenant();
  const [currentIndex, setCurrentIndex] = useState(0);
  const [selectedEventId, setSelectedEventId] = useState<string | null>(null);
  const { materials } = useMaterials();

  const { data: rawExamplesData, isLoading: isLoadingExamples } =
    useGetMaterialExamplesQuery(
      {
        tenantName: tenant,
        materialName,
        location: location ?? "",
        limit: 10,
      },
      { skip: !location }
    );

  const examplesData = rawExamplesData;

  const currentPair = useMemo<MaterialEventPair | null>(() => {
    if (!examplesData?.pairs || examplesData.pairs.length === 0) {
      return null;
    }
    return examplesData.pairs[currentIndex] ?? null;
  }, [examplesData, currentIndex]);

  const videoWindow = useMemo(() => {
    if (!currentPair) return null;

    const liftStartTime = dayjs.utc(currentPair.lift.data.start);
    const dropEndTime = dayjs.utc(currentPair.drop.data.end);

    return calculateVideoWindow(liftStartTime, dropEndTime);
  }, [currentPair]);

  const events = useMemo<ScrapEyesEvent[]>(() => {
    if (!currentPair || !videoWindow) return [];

    const liftStartTime = dayjs.utc(currentPair.lift.data.start);
    const liftEndTime = dayjs.utc(currentPair.lift.data.end);
    const dropStartTime = dayjs.utc(currentPair.drop.data.start);
    const dropEndTime = dayjs.utc(currentPair.drop.data.end);

    const liftMaterial = getEventLabel(currentPair.lift.labels, "material");
    const liftArea = getEventLabel(currentPair.lift.labels, "area");

    const dropMaterial = getEventLabel(currentPair.drop.labels, "material");
    const dropArea = getEventLabel(currentPair.drop.labels, "area");

    return withAreaLabels([
      {
        id: String(currentPair.lift.id),
        start: liftStartTime,
        end: liftEndTime,
        eventType: currentPair.lift.data.event_type,
        material: liftMaterial,
        area: liftArea,
      },
      {
        id: String(currentPair.drop.id),
        start: dropStartTime,
        end: dropEndTime,
        eventType: currentPair.drop.data.event_type,
        material: dropMaterial,
        area: dropArea,
      },
    ]);
  }, [currentPair, videoWindow, withAreaLabels]);

  const handleNext = () => {
    if (examplesData?.pairs) {
      setCurrentIndex((prev) =>
        prev < examplesData.pairs.length - 1 ? prev + 1 : prev
      );
    }
  };

  const handlePrevious = () => {
    setCurrentIndex((prev) => (prev > 0 ? prev - 1 : prev));
  };

  if (isLoadingExamples) {
    return <LoadingState minHeight={300} />;
  }

  if (!examplesData || !currentPair || !videoWindow || !location) {
    return <EmptyState minHeight={300} />;
  }

  return (
    <Stack spacing={2}>
      <Box sx={{ display: "flex", gap: 2, alignItems: "flex-start" }}>
        <Box sx={{ flex: 1 }}>
          <VideoControlProvider key={currentPair.pair_id}>
            <ExampleVideoPlayerContent
              location={location}
              startIso={videoWindow.startIso}
              endIso={videoWindow.endIso}
              events={events}
              videoStart={videoWindow.videoStart}
            />
          </VideoControlProvider>
        </Box>
        <ScrapEyesEventsPanel
          events={events}
          selectedEventId={selectedEventId}
          recordingEventId={null}
          onSelectEvent={setSelectedEventId}
          materialOptions={materials ?? []}
          location={location}
        />
      </Box>
      <ExampleNavigationControls
        currentIndex={currentIndex}
        totalCount={examplesData.pairs.length}
        onPrevious={handlePrevious}
        onNext={handleNext}
      />
    </Stack>
  );
};
